import React, { Component } from 'react';
import { createIncident, updateIncident } from '../../../api/incidents';
import Input from '../../elements/Input';
import Select from '../../elements/Select';
import Textarea from '../../elements/Textarea';
import ModalBody from '../modal/ModalBody';
import ModalControls from '../modal/ModalControls';
import ModalFooter from '../modal/ModalFooter';
import ModalHeader from '../modal/ModalHeader';
import type { Incident, IncidentListItem, Project, User } from '../../../types';

const CHANNEL_OPTIONS = ['email', 'phone', 'slack', 'other'];

type State = {
  form: {
    project_id: number | string;
    title: string;
    reported_by: string;
    reported_at: string;
    reported_to_id: number | string;
    reported_via: string;
    approved_by: string;
    approved_at: string;
    description: string;
    status: string;
  };
  errors: Record<string, string>;
  loading: boolean;
};

type Props = {
  disableButtons?: boolean;
  projects: Array<Project>;
  users: Array<User>;
  currentUser: User;
  onSubmit?: (incident: Incident) => void;
  onCloseModal: () => void;
  edit?: boolean;
  incident?: Incident | IncidentListItem;
};

class IncidentModal extends Component<Props, State> {
  static defaultProps: Record<string, unknown> = {
    disableButtons: false,
    edit: false,
  };

  state: State = {
    form: {
      project_id: '',
      title: '',
      reported_by: '',
      reported_at: new Date().toISOString().slice(0, 16),
      reported_to_id: '',
      reported_via: 'other',
      approved_by: '',
      approved_at: new Date().toISOString().slice(0, 16),
      description: '',
      status: 'unresolved',
    },
    errors: {},
    loading: false,
  };

  getProjectUsers = () => {
    const { users: allUsers } = this.props;
    const { form } = this.state;

    if (!form.project_id) {
      return [];
    }

    const selectedProjectId = Number(form.project_id);

    return allUsers.filter(u =>
      u.active && u.projectIds.includes(selectedProjectId)
    );
  };

  componentDidMount() {
    const { projects, currentUser, edit, incident } = this.props;
    
    if (edit && incident) {
      // Pre-populate form with existing incident data
      this.setState({
        form: {
          project_id: incident.project_id || '',
          title: incident.title || '',
          reported_by: incident.reported_by || '',
          reported_at: incident.reported_at ? new Date(incident.reported_at).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16),
          reported_to_id: incident.reported_to_id || '',
          reported_via: incident.reported_via || 'other',
          approved_by: incident.approved_by || '',
          approved_at: incident.approved_at ? new Date(incident.approved_at).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16),
          description: incident.description || '',
          status: incident.status || 'unresolved',
        },
      });
    } else {
      // Set default project for new incidents
      const userProjects = projects.filter(p =>
        currentUser.projectIds.includes(p.id)
      );

      if (userProjects.length > 0) {
        this.setState({
          form: {
            ...this.state.form,
            project_id: userProjects[0].id,
          },
        });
      }
    }
  }

  isFormValid = () => {
    const { form } = this.state;

    return (
      !!form.project_id &&
      !!form.title.trim() &&
      !!form.reported_by.trim() &&
      !!form.reported_to_id
    );
  };

  handleInputChange = (field: string, value: any) => {
    this.setState(prevState => ({
      form: {
        ...prevState.form,
        [field]: value,
      },
    }));
  };

  handleSubmit = async () => {
    const { form } = this.state;
    const { onCloseModal, onSubmit, edit, incident } = this.props;

    if (!form.title.trim()) {
      this.setState({ errors: { title: 'Title is required' } });

      return;
    }

    if (!form.project_id) {
      this.setState({ errors: { project_id: 'Project is required' } });

      return;
    }

    if (!form.reported_by.trim()) {
      this.setState({ errors: { reported_by: 'Reported by is required' } });

      return;
    }

    if (!form.reported_to_id) {
      this.setState({ errors: { reported_to_id: 'Reported to is required' } });

      return;
    }

    this.setState({ loading: true, errors: {} });

    try {
      const incidentData = {
        title: form.title,
        reported_by: form.reported_by,
        reported_at: form.reported_at,
        reported_to_id: form.reported_to_id ? Number(form.reported_to_id) : null,
        reported_via: form.reported_via,
        approved_by: form.approved_by,
        approved_at: form.approved_at,
        description: form.description,
        status: form.status as 'unresolved' | 'resolved',
      };

      let updatedIncident: Incident;

      if (edit && incident) {
        // Update existing incident with PUT request
        updatedIncident = await updateIncident(Number(form.project_id), incident.id, incidentData);
      } else {
        // Create new incident with POST request
        updatedIncident = await createIncident(Number(form.project_id), incidentData);
      }

      if (onSubmit) {
        onSubmit(updatedIncident);
      }
      onCloseModal();
    } catch (error: any) {
      this.setState({
        errors: error.response?.data || { error: edit ? 'Failed to update incident' : 'Failed to create incident' },
        loading: false,
      });
    }
  };

  render() {
    const { form, errors, loading } = this.state;
    const { disableButtons, projects, currentUser, onCloseModal, edit } = this.props;

    const userProjects = projects.filter(p =>
      currentUser.projectIds.includes(p.id)
    );

    return (
      <>
        <ModalHeader title={edit ? "Update Incident" : "Report Incident"} onCloseClick={onCloseModal} />
        <ModalBody>
          <form>
            <Select
              name="project_id"
              id="project_id"
              label="Project"
              selectClass="form-control"
              value={form.project_id}
              items={userProjects.map(p => ({ id: p.id, title: p.title }))}
              onSelectChange={(e) => {
                this.handleInputChange('project_id', e.target.value);
              }}
              includeChooseOption={false}
            />
            {errors.project_id && (
              <div className="text-danger mt-2">{errors.project_id}</div>
            )}

            <Input
              name="title"
              id="title"
              label="Title"
              value={form.title}
              onChange={(e) => {
                this.handleInputChange('title', e.target.value);
              }}
            />
            {errors.title && (
              <div className="text-danger mt-2">{errors.title}</div>
            )}

            <Textarea
              name="description"
              id="description"
              label="Description"
              value={form.description}
              onChange={(e) => {
                this.handleInputChange('description', e.target.value);
              }}
            />

            <Input
              name="reported_by"
              id="reported_by"
              label="Reported by"
              value={form.reported_by}
              onChange={(e) => {
                this.handleInputChange('reported_by', e.target.value);
              }}
            />
            {errors.reported_by && (
              <div className="text-danger mt-2">{errors.reported_by}</div>
            )}

            <Input
              name="reported_at"
              id="reported_at"
              label="Reported at"
              type="datetime-local"
              value={form.reported_at}
              onChange={(e) => {
                this.handleInputChange('reported_at', e.target.value);
              }}
            />

            <Select
              name="reported_to_id"
              id="reported_to_id"
              label="Reported to"
              selectClass="form-control"
              value={form.reported_to_id}
              items={[
                { id: '', title: 'Select a user...' },
                ...this.getProjectUsers().map(u => ({ id: u.id, title: `${u.name} ${u.surname}` })),
              ]}
              onSelectChange={(e) => this.handleInputChange('reported_to_id', e.target.value)}
              includeChooseOption={false}
            />
            {errors.reported_to_id && (
              <div className="text-danger mt-2">{errors.reported_to_id}</div>
            )}

            <Select
              name="reported_via"
              id="reported_via"
              label="Reported via"
              selectClass="form-control"
              value={form.reported_via}
              items={CHANNEL_OPTIONS.map(channel => ({
                id: channel.toLowerCase(),
                title: channel.charAt(0).toUpperCase() + channel.slice(1),
              }))}
              onSelectChange={(e) => {
                this.handleInputChange('reported_via', e.target.value);
              }}
              includeChooseOption={false}
            />

            <Input
              name="approved_by"
              id="approved_by"
              label="Approved by"
              value={form.approved_by}
              onChange={(e) => {
                this.handleInputChange('approved_by', e.target.value);
              }}
            />

            <Input
              name="approved_at"
              id="approved_at"
              label="Approved at"
              type="datetime-local"
              value={form.approved_at}
              onChange={(e) => {
                this.handleInputChange('approved_at', e.target.value);
              }}
            />

            <Select
              name="status"
              id="status"
              label="Status"
              selectClass="form-control"
              value={form.status}
              items={[
                { id: 'unresolved', title: 'Unresolved' },
                { id: 'resolved', title: 'Resolved' },
              ]}
              onSelectChange={(e) => {
                this.handleInputChange('status', e.target.value);
              }}
              includeChooseOption={false}
            />
          </form>
        </ModalBody>
        <ModalFooter>
          <ModalControls
            disableButtons={disableButtons || loading}
            isFormValid={!this.isFormValid()}
            showEditButton={false}
            submitButtonText={edit ? "Update Incident" : "Create Incident"}
            onSubmitButtonClick={this.handleSubmit}
            onCloseButtonClick={onCloseModal}
          />
        </ModalFooter>
      </>
    );
  }
}

export default IncidentModal;
